import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import Button from '../../../components/ui/Button'
import type { MeetingGroup } from '../types'

interface ColumnOpts {
  t: TFunction
  busyId: number | null
  onEdit: (row: MeetingGroup) => void
  onDelete: (row: MeetingGroup) => void
  /** 077 — Grup No kolonu yalnız RECORD_NUMBER_VIEW yetkisinde gösterilir (Özel Kalem). */
  showRecordNumber: boolean
  /** 126 — Kısıtlı Yetkili asistan: düzenle/sil kolonu hiç çizilmez (salt okunur). */
  readOnly?: boolean
}

/**
 * Toplantı Grupları yönetim tablosunun sütunları (041): numara + ad + açıklama + kayıt grubu (122) + aktif
 * toplantı sayısı + düzenle/sil. İlk kolon grup numarasıdır (analiz 12; ör. `TG-2026-0001`) — sunucu üretir, düzenlenemez.
 * Silme, altında aktif toplantı olan grupta backend'de engellenir (409 → kullanıcıya mesaj). Boş hücreler
 * yer-tutucusuz (Page Standards).
 */
export function meetingGroupColumns({
  t,
  busyId,
  onEdit,
  onDelete,
  showRecordNumber,
  readOnly = false,
}: ColumnOpts): ReactElement[] {
  return [
    ...(showRecordNumber
      ? [
          <Column
            key="recordNumber"
            field="recordNumber"
            header={t('meetingGroups.columns.number')}
            style={{ width: '11rem' }}
          />,
        ]
      : []),
    <Column key="name" field="name" header={t('meetingGroups.columns.name')} />,
    <Column
      key="description"
      field="description"
      header={t('meetingGroups.columns.description')}
      body={(row: MeetingGroup) => row.description ?? ''}
    />,
    <Column
      key="groupName"
      field="groupName"
      header={t('recordGroups.column')}
      style={{ width: '14rem' }}
      body={(row: MeetingGroup) => row.groupName ?? ''}
    />,
    <Column
      key="meetingCount"
      field="meetingCount"
      header={t('meetingGroups.columns.meetingCount')}
      style={{ width: '11rem' }}
    />,
    ...(readOnly ? [] : [actionsColumn({ t, busyId, onEdit, onDelete })]),
  ]
}

/** Düzenle/sil kolonu — yalnız yazma işlemleri taşıdığı için salt okunur kipte hiç eklenmez. */
function actionsColumn({
  t,
  busyId,
  onEdit,
  onDelete,
}: Pick<ColumnOpts, 't' | 'busyId' | 'onEdit' | 'onDelete'>): ReactElement {
  return (
    <Column
      key="actions"
      header={t('meetingGroups.columns.actions')}
      style={{ width: '9rem' }}
      body={(row: MeetingGroup) => (
        <div className="admin-row-actions">
          <Button
            icon="pi pi-pencil"
            rounded
            text
            aria-label={t('meetings.actions.edit')}
            tooltip={t('meetings.actions.edit')}
            tooltipOptions={{ position: 'bottom' }}
            disabled={busyId === row.id}
            onClick={() => onEdit(row)}
          />
          <Button
            icon="pi pi-trash"
            rounded
            text
            severity="danger"
            aria-label={t('meetings.actions.delete')}
            tooltip={t('meetings.actions.delete')}
            tooltipOptions={{ position: 'bottom' }}
            disabled={busyId === row.id}
            onClick={() => onDelete(row)}
          />
        </div>
      )}
    />
  )
}
